import type { ImportColumnDecision, ImportItemType } from "@lafayette/import-mapping";
import { STAFF_IMPORT_BOUNDS } from "@lafayette/schemas";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { subheading } from "../../catalog/components/styles";
import { itemTypeLabels } from "../../catalog/labels";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { DataTable } from "../../ui/data-table";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { columnLabel, type ImportColumnView } from "../columns";
import { StepCard } from "../components/StepCard";
import { emptyValue, rawText, stepActions } from "../components/styles";
import {
  proposalFor,
  proposedValues,
  type ReviewField,
  type ReviewRow,
  rawValues,
  reviewFields,
} from "../review";

type ReviewStepProps = Readonly<{
  columns: readonly ImportColumnView[];
  decisions: readonly ImportColumnDecision[];
  defaultItemType: ImportItemType | null;
  rows: readonly ReviewRow[];
  /** The job was submitted: show the proposal the server saved. */
  useSaved: boolean;
  /** The rows come from the first rows of a new file (cut cells), not from a saved draft. */
  fromPreview: boolean;
  loading: boolean;
  errorMessage: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  onContinue: () => void;
}>;

function Raw({ values }: Readonly<{ values: readonly string[] }>) {
  const shown = values.length === 1 ? values : values.filter((value) => value !== "");
  if (shown.length === 0 || shown.every((value) => value === "")) {
    return <span className={emptyValue}>(empty)</span>;
  }
  return shown.length === 1 ? (
    <span data-raw="" className={rawText}>
      {shown[0]}
    </span>
  ) : (
    <ul data-values="" className="m-0 grid list-none gap-1 p-0">
      {shown.map((value, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: cells may repeat.
        <li key={index} data-raw="" className={rawText}>
          {value}
        </li>
      ))}
    </ul>
  );
}

function Proposed({ values }: Readonly<{ values: readonly string[] }>) {
  if (values.length === 0) return <span className={emptyValue}>(empty)</span>;
  return values.length === 1 ? (
    <strong>{values[0]}</strong>
  ) : (
    <ul data-values="" className="m-0 grid list-none gap-1 p-0">
      {values.map((value, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: values may repeat.
        <li key={index}>
          <strong>{value}</strong>
        </li>
      ))}
    </ul>
  );
}

const tag = "text-[11px] tracking-wider text-muted-foreground uppercase";

/** One row with its proposal, worked out for this render. The columns read it, so the column list changes only with the mapped fields. */
type ReviewViewRow = Readonly<{ row: ReviewRow; proposal: ReturnType<typeof proposalFor> }>;

const column = createColumnHelper<ReviewViewRow>();

function reviewColumns(fields: readonly ReviewField[]): ColumnDef<ReviewViewRow, unknown>[] {
  return [
    column.display({
      id: "row",
      header: "Row",
      meta: { rowHeader: true },
      cell: (info) => info.row.original.row.sourceRowNumber ?? info.row.original.row.rowNumber,
    }),
    column.display({
      id: "itemType",
      header: "Item type",
      cell: (info) => {
        const { proposal } = info.row.original;
        return proposal.itemType === null ? (
          <span className={emptyValue}>Not set</span>
        ) : (
          <strong>{itemTypeLabels[proposal.itemType]}</strong>
        );
      },
    }),
    ...fields.map((field) =>
      column.display({
        id: field.target.key,
        header: field.target.label,
        meta: { className: "min-w-44 align-top" },
        cell: (info) => {
          const { row, proposal } = info.row.original;
          return (
            <>
              <div className="mb-2 grid gap-0.5" data-cell-line="">
                <span className={tag}>Raw</span>
                <Raw values={rawValues(row, field)} />
              </div>
              <div className="grid gap-0.5" data-cell-line="">
                <span className={tag}>Proposed</span>
                <Proposed values={proposedValues(proposal, field)} />
              </div>
            </>
          );
        },
      }),
    ),
  ] as ColumnDef<ReviewViewRow, unknown>[];
}

/**
 * Step 4. Each source row with the raw cells and the proposal side by side. The proposal comes from
 * `buildProposal` of the import-mapping package: the same function the server runs at submit. It is a
 * proposal only; nothing reaches the catalog.
 */
export function ReviewStep({
  columns,
  decisions,
  defaultItemType,
  rows,
  useSaved,
  fromPreview,
  loading,
  errorMessage,
  hasMore,
  loadingMore,
  onLoadMore,
  onRetry,
  onContinue,
}: ReviewStepProps) {
  const fields = reviewFields(decisions);
  const fieldKey = fields.map((field) => field.target.key).join("|");
  // biome-ignore lint/correctness/useExhaustiveDependencies: the key names the mapped fields; the list is rebuilt only when it changes
  const tableColumns = useMemo(() => reviewColumns(fields), [fieldKey]);
  const summary = decisions.map((decision) => {
    const column = columns.find((entry) => entry.index === decision.columnIndex);
    return {
      decision,
      label: column === undefined ? `Column ${decision.columnIndex + 1}` : columnLabel(column),
    };
  });
  const viewRows: ReviewViewRow[] = rows.map((row) => ({
    row,
    proposal: proposalFor(row, decisions, defaultItemType, useSaved),
  }));

  return (
    <StepCard step="review" title="Review">
      <p>
        The raw value is what the file holds. The proposed value is the tidied text the mapping
        makes from it: no meaning changes, and nothing reaches the catalog.
        {fromPreview
          ? ` These are the first rows of the file. Cells are cut to ${STAFF_IMPORT_BOUNDS.previewCellChars} characters.`
          : ""}
      </p>

      <section aria-labelledby="import-summary-title" className="grid gap-2">
        <h3 id="import-summary-title" className={subheading}>
          Column summary
        </h3>
        <ul className="m-0 grid gap-1 pl-5">
          {summary.map(({ decision, label }) => (
            <li key={decision.columnIndex}>
              {label} →{" "}
              {decision.decision === "MAPPED" ? (
                <strong>
                  {fields.find((field) => field.target.key === decision.targetField)?.target
                    .label ?? decision.targetField}
                </strong>
              ) : decision.decision === "IGNORED" ? (
                "ignored"
              ) : (
                <em>not mapped</em>
              )}
            </li>
          ))}
        </ul>
      </section>

      {loading ? (
        <p role="status">Reading the rows…</p>
      ) : errorMessage !== null ? (
        <div
          className="grid gap-2 rounded-sm border border-destructive-border bg-destructive-soft p-4"
          role="alert"
        >
          <p className="m-0 flex items-start gap-2 font-semibold text-destructive">
            <Icon name="alert-02" className="mt-1 size-4 shrink-0" />
            <span>{errorMessage}</span>
          </p>
          <div>
            <Button type="button" onClick={onRetry}>
              Try again
            </Button>
          </div>
        </div>
      ) : fields.length === 0 ? (
        <Notice tone="warning" role="status">
          No column is mapped yet. Map columns to see the proposal.
        </Notice>
      ) : rows.length === 0 ? (
        <p className={cn("m-0 text-sm text-muted-foreground")}>
          The file has no data rows under the header row.
        </p>
      ) : (
        <>
          <DataTable
            banded
            columns={tableColumns}
            data={viewRows}
            getRowId={(entry) => String(entry.row.rowNumber)}
            caption={`Raw values beside proposed values, ${rows.length} ${rows.length === 1 ? "row" : "rows"} shown.`}
          />
          <div className="flex flex-wrap items-center gap-4">
            <p role="status" className="m-0 text-sm text-muted-foreground">
              Showing {rows.length} {rows.length === 1 ? "row" : "rows"}
              {hasMore ? ". More are available." : "."}
            </p>
            {hasMore ? (
              <Button type="button" variant="secondary" onClick={onLoadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more rows"}
              </Button>
            ) : null}
          </div>
        </>
      )}

      <div className={stepActions}>
        <Button type="button" onClick={onContinue}>
          Continue to save
        </Button>
      </div>
    </StepCard>
  );
}
